import { Flex, Box, Text } from "@chakra-ui/react";
import { Modal, Row, Col } from "antd";
import PrimaryButton from "components/Button/PrimaryButton";
import Field from "components/Input/Field/index";
import { useForm } from "react-hook-form";
import SelectFormaPagamento from "components/Input/SelectFormaPagamento";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import { formatMoney } from "infra/helpers/number";
import { ContaItemResponse } from "features/conta/typings";
import ItemContaLiquidar from "./components/ItemContaLiquidar/index";
import {
  PagarMultiplasContasForm,
  ContaPagarMultiplasContasForm,
} from "core/features/conta/typings";
import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import OutlineButton from "ui/components/Button/OutlineButton";
import { useEffect, useState } from "react";
import { useDebounce } from "features/shared/useDebouce";
import { OperacaoEnum } from "core/resources/enums/conta";
import moment from "moment";
import usePagarContasMultiplas from "core/features/conta/hooks/usePagarContasMultiplas";

interface ModalLiquidarContasProps {
  isOpenModal: boolean;
  onClose: () => any;
  data: ContaItemResponse[];
  refetch: any;
  operacao?: OperacaoEnum | undefined;
}

const defaultValues: Partial<PagarMultiplasContasForm> = {
  dataPagamento: moment().format("YYYY-MM-DD"),
};

export function ModalLiquidarContas({
  isOpenModal,
  onClose,
  data,
  refetch,
  operacao,
}: ModalLiquidarContasProps) {
  const [isLoadingEncargo, setIsLoadingEncargo] = useState<
    Record<string, boolean>
  >({});

  const { mutate: handleSave, isLoading: isLoadingSave } =
    usePagarContasMultiplas();

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<PagarMultiplasContasForm>({
      defaultValues,
    });

  const form = watch();

  const getValorPago = (conta: ContaPagarMultiplasContasForm) => {
    return (
      conta.valor +
      (conta?.juros || 0) +
      (conta?.multa || 0) +
      (form?.taxaAdicionalCategoriaOperacao === OperacaoEnum.Receita
        ? conta?.taxaAdicional ?? 0
        : 0)
    );
  };

  useEffect(() => {
    if (data) {
      reset({
        ...defaultValues,
        contas: data.map((item) => ({
          id: item.id,
          valor: item.valorOriginal,
        })),
      });
    }
  }, [data, reset]);

  const onSubmit = async (data: PagarMultiplasContasForm) => {
    const _data = {
      ...data,
      contas: data.contas.map((conta) => ({
        ...conta,
        valorPago: getValorPago(conta),
      })),
    };

    handleSave(_data, {
      onSuccess: () => {
        showSuccess().then(() => {
          refetch();
          onClose();
        });
      },
      onError: (e) => {
        showErrorAxios(e);
      },
    });
  };

  const debouncedDataPagamento = useDebounce(form.dataPagamento, 150);

  const valorTotal =
    form.contas?.reduce((acc, value) => {
      return acc + getValorPago(value);
    }, 0) || 0;

  const _isLoadingEncargo = Object.values(isLoadingEncargo).some((v) => !!v);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal
        centered
        destroyOnClose
        width="1200px"
        title="Liquidar conta"
        footer={
          <Flex flex={1} justifyContent="end" gap={4}>
            <OutlineButton onClick={onClose}>Fechar</OutlineButton>
            <PrimaryButton
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoadingSave || _isLoadingEncargo}
            >
              Liquidar
            </PrimaryButton>
          </Flex>
        }
        visible={isOpenModal}
        onCancel={onClose}
      >
        <Box maxH="75vh">
          <Box as={Row} gutter={16} mb={4} pr={2}>
            <Box as={Col} xs={operacao === OperacaoEnum.Receita ? 4 : 6}>
              <Field
                control={control}
                name={"dataPagamento"}
                label="Data do pagamento"
                rules={{
                  required: true,
                }}
                inputProps={{
                  type: "date",
                }}
              />
            </Box>
            <Box as={Col} xs={operacao === OperacaoEnum.Receita ? 4 : 6}>
              <Field
                control={control}
                name={"formaPagamentoId"}
                label="Forma de pagamento"
                rules={{
                  required: true,
                }}
                render={({ field, isInvalid }) => (
                  <SelectFormaPagamento
                    isInvalid={isInvalid}
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </Box>
            <Box as={Col} xs={operacao === OperacaoEnum.Receita ? 4 : 6}>
              <Field
                shouldUnregister
                control={control}
                name={"tenantContaId"}
                label="Conta bancária"
                rules={{
                  required: true,
                }}
                render={({ field, isInvalid }) => (
                  <SelectContaBancaria
                    isInvalid={isInvalid}
                    onChange={field.onChange}
                    value={field.value}
                    incluirInativas={false}
                  />
                )}
              />
            </Box>
            <Box as={Col} xs={6}>
              <Field
                control={control}
                name={"dataMovimentacao"}
                label={`Movimentação de entrada no banco`}
                inputProps={{
                  type: "date",
                }}
              />
            </Box>
            {operacao === OperacaoEnum.Receita ? (
              <Box as={Col} xs={6}>
                <Field
                  control={control}
                  name={"taxaAdicionalCategoriaId"}
                  label="Categoria da Taxa Adicional"
                  rules={{
                    required: form.contas?.some(
                      (conta) => conta.taxaAdicional && conta.taxaAdicional > 0
                    ),
                  }}
                  render={({ field, isInvalid }) => (
                    <SelectContaCategoria
                      disableNotClassifiableNodes
                      isInvalid={isInvalid}
                      value={field.value}
                      onChange={(v, item) => {
                        field.onChange(v);
                        setValue(
                          "taxaAdicionalCategoriaOperacao",
                          item?.operacao ?? null
                        );
                      }}
                    />
                  )}
                />
              </Box>
            ) : null}
          </Box>

          <Flex justifyContent="space-between" mb={4} mr={2}>
            <Text fontWeight="medium">Contas</Text>
            <Flex>
              <Text fontWeight="medium" mr={2}>
                Valor total a pagar:
              </Text>
              <Text fontWeight="bold">{formatMoney(valorTotal)}</Text>
            </Flex>
          </Flex>

          <Box overflowY="auto" pr={2} className="custom-scroll-bar" h="62vh">
            {data?.map((item, index) => (
              <ItemContaLiquidar
                formItem={form.contas?.[index]}
                item={item}
                key={item.id}
                index={index}
                dataPagamento={debouncedDataPagamento}
                control={control}
                operacao={operacao}
                setValue={setValue}
                setLoadingEncargos={setIsLoadingEncargo}
                taxaAdicionalCategoriaOperacao={
                  form.taxaAdicionalCategoriaOperacao
                }
              />
            ))}
          </Box>
        </Box>
      </Modal>
    </form>
  );
}
